/**
 * @voltxampere/engine — Formula template: voltage divider (Sprint 56b)
 *
 * Subgraph: R1 + R2 series with V_in tap above R1 and GND below R2.
 * The midpoint between the two resistors is the divider output.
 *
 *     V_in ── R1 ── V_out ── R2 ── GND
 *
 * The match requires the divider midpoint to be wired to exactly
 * the two resistor terminals — extra fan-out (an op-amp input
 * sensing the tap) is allowed; an extra current path is not (or it
 * would not be a clean divider).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, buildNetIndex, componentsOfType, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, } from "../helpers.js";
const SYMBOLIC = "V_{out} = V_{in} \\cdot \\frac{R_2}{R_1 + R_2}";
export const voltageDividerTemplate = {
    id: "voltage-divider",
    specificity: "low",
    displayNameKey: {
        tr: "formula.voltage-divider.name",
        en: "formula.voltage-divider.name",
    },
    match(circuit, selected) {
        const resistors = componentsOfType(circuit, "resistor", selected);
        if (resistors.length < 2)
            return null;
        const netIndex = buildNetIndex(circuit);
        for (const r1 of resistors) {
            const r1P1 = netOf(r1, "p1");
            const r1P2 = netOf(r1, "p2");
            if (r1P1 === null || r1P2 === null)
                continue;
            for (const r2 of resistors) {
                if (r1.id === r2.id)
                    continue;
                const r2P1 = netOf(r2, "p1");
                const r2P2 = netOf(r2, "p2");
                if (r2P1 === null || r2P2 === null)
                    continue;
                // Try every (r1Top, r1Tap) ordering against (r2Tap, r2Bot).
                // r1Tap must equal r2Tap for the divider midpoint.
                const candidates = [
                    { r1Top: r1P1, tap: r1P2, r2Bot: r2P1 === r1P2 ? r2P2 : r2P1 },
                    { r1Top: r1P2, tap: r1P1, r2Bot: r2P1 === r1P1 ? r2P2 : r2P1 },
                ];
                for (const cand of candidates) {
                    if (cand.tap !== r2P1 && cand.tap !== r2P2)
                        continue;
                    // r1Top must carry a voltage source; r2Bot must be ground.
                    if (!isGroundNet(circuit, cand.r2Bot))
                        continue;
                    const vSource = findVoltageSourceDriving(circuit, cand.r1Top);
                    if (vSource === null)
                        continue;
                    // The divider tap must not have an alternative current path
                    // (only r1 and r2 terminals attach to it). Ignore non-
                    // current-carrying probes (op-amp non-inverting inputs are
                    // ideal so they don't violate this — but we don't model
                    // input bias here; defer that to specific op-amp templates).
                    const tapTerminals = netIndex.get(cand.tap) ?? [];
                    // Allow at most r1 + r2 OR an additional sense terminal
                    // belonging to an op-amp input (high-Z) — but we
                    // conservatively reject when there are non-resistor /
                    // non-opamp terminals.
                    // High-impedance probes (op-amp inputs, voltmeters, ideal
                    // comparator inputs) do not draw current and therefore do
                    // not invalidate the divider equation. Diodes also fan-out
                    // without disturbing the DC operating point when they are
                    // reverse-biased, which is the default state for a bridge
                    // rectifier's inputs sitting at the divider tap.
                    const HIGH_Z_PROBE_TYPES = new Set([
                        "op-amp",
                        "opamp",
                        "voltmeter",
                        "diode", // bridge rectifier inputs at the sense tap
                    ]);
                    const extraCurrent = tapTerminals.filter((t) => t.component.id !== r1.id &&
                        t.component.id !== r2.id &&
                        !HIGH_Z_PROBE_TYPES.has(t.component.type));
                    if (extraCurrent.length > 0)
                        continue;
                    const r1Val = asNumber(r1.params.resistance);
                    const r2Val = asNumber(r2.params.resistance);
                    const vInVal = asNumber(vSource.params.voltage);
                    if (r1Val === null || r2Val === null || vInVal === null)
                        continue;
                    if (r1Val + r2Val === 0)
                        continue;
                    const vOut = (vInVal * r2Val) / (r1Val + r2Val);
                    const r1LaTeX = formatLatexValue(r1Val, "\\Omega");
                    const r2LaTeX = formatLatexValue(r2Val, "\\Omega");
                    const vInLaTeX = formatLatexValue(vInVal, "V");
                    const vOutLaTeX = formatLatexValue(vOut, "V");
                    return {
                        templateId: "voltage-divider",
                        involvedComponents: [r1.id, r2.id, vSource.id],
                        involvedNets: [cand.r1Top, cand.tap, cand.r2Bot],
                        symbolic: SYMBOLIC,
                        numeric: {
                            formula: `V_{out} = ${vInLaTeX} \\cdot \\frac{${r2LaTeX}}{${r1LaTeX} + ${r2LaTeX}} = ${vOutLaTeX}`,
                            result: vOut,
                            unit: "V",
                        },
                        specificity: "low",
                    };
                }
            }
        }
        return null;
    },
};
//# sourceMappingURL=voltage-divider.js.map